import { TSuggest } from 'feature/registration/types'
import { Status } from 'feature/statuses/types'
import { TUserDataForOrder } from 'feature/user/types'

export type OrderTypes =
  | 'product_order'
  | 'service_maintenance'
  | 'purchase_advice'
  | 'warranty'

export type TAppeal = {
  uid: number
  orderId: string
  number: string
  status: {
    status: AppealStatus
    name: string
  }
  hasPayment: boolean
  name: string
  type: TAppealType
  typeName: string
  products: TProduct[]
  location: TSuggest
  user: TUserDataForOrder
  timeSlots: TTimeSlot[] | null
  dealerCode: string | null
  cancelReasonComment: string
  createdDate: string
  updatedDate: string
  dealer: TDealer | null
  visit: TAppealVisit | null
  offers: TAppealOffer[]
  service: TAppealService | null
  orderIdList: Array<string>
}

export type TProductParams = TParametrs & {
  parameterValue: {
    displayValue: string | null
    value: string | number | boolean | null
  }
}

export type TProductFile = {
  uid: string
  url: string
  size: number
  filename: string
  mimetype: string
}

export type TProduct = {
  id: number
  productType: string | null
  name: string
  parameters: TProductParams[] | []
  blankedParameters: TProductParams[] | []
  files: TProductFile[] | []
}

export type TParametrs = {
  id: number
  typeCode: 'NUMBER' | 'DROPDOWN' | 'CHECKBOX' | 'IMAGES' | 'TEXT'
  name: string
  iconUrl: string
  editable: boolean
  answerList?: {
    id: number
    value: string
  }[]
  typeValues?: {
    id: number
    value: string
  }[]
}

export type TAppealVisit = {
  canGrade: boolean
  date: string | null
  status: 'PLANNED' | 'OFFER' | 'PAID'
  grade: TAppealGrade
  offer: TServiceOffer | null
}

export type TServiceOffer = {
  id: number
  createdDate: string
  expireDate: string
  payDate: string
  price: number
  acquiringUrl: string
  acquiringSecondUrl: string | null
  offerUrl: string
  checkUrl: string
  isFree: boolean
  estimatedOrderUrl: string
  contractUrl: string
  isFullPrepayment: boolean
  payments: TServiceOfferPrice[]
  checks: {
    id: number
    type: string
    amount: number
    url: string
  }[]
}

export type TServiceOfferPrice = {
  amount: number
  isPaid: boolean
  receiptType: string
}

export type TAppealOfferParams = TParametrs & {
  value: string
}

export type TAppealOffer = {
  status: 'APPROVED' | 'DECLAINED' | 'SENT' | 'CANCELED' | 'UNKNOWN'
  id: number
  products: [
    {
      productType: string
      count: number
      price: number
      name: string
      imageUrl: string
      parameters: TAppealOfferParams[]
    },
  ]
  offer: TServiceOffer | null
  service: {
    name: string
    price: number
  }[]
  recalcInProgress: boolean
}

export type TDealer = {
  uid: number
  name: string
  isTechnicalDepartment: boolean
  phone: string
  email: string
}

export type TTimeSlot = {
  dateFrom: string
  dateTo: string
  id: number
  key: string
}

export type TAppealService = {
  date: string
  status: string
  canGrade: boolean
  grade: TAppealGrade
}

export type TAppealGrade = {
  value: number
  comment: string
  link: string | null
  files?: TProductFile[]
}

export type TAppealType = 'ORDERING' | 'SERVICE' | 'CONSULTATION'

export type AppealsType = 'активные' | 'завершенные'
export type AppealsLabel =
  | 'Создано'
  | 'Ожидает оплаты услуги'
  | 'Отменено'
  | 'Завершено'

export type TTimeSlotListItem = {
  id: number
  from: string
  to: string
  disabled?: boolean
}

export type TTimeSlotList = {
  list: TTimeSlotListItem[]
}

export type AppealStatus =
  | 'PREPROCESSING'
  | 'DEALER_SELECTED'
  | 'VISIT_OFFER'
  | 'VISIT_PAID'
  | 'OFFER'
  | 'OFFER_PAID'
  | 'PROCESSING'
  | 'GRADE_WAITING'
  | 'FIXING'
  | 'COMPLETED'
  | 'CANCELED'
  | 'UNKNOWN'
  | 'AWAITING_PAYMENT'
  | 'WAITING_DEALER'
  | 'SCHEDULED'

export type OrderListArrayProps = {
  id: number
  number: string
  title: string
  orderType: OrderTypes
  status?: {
    label: AppealsLabel
    status: Status
  }
}

export type TAppealInList = {
  uid: number
  orderId: number
  number: number
  status: {
    name: string
    status: AppealStatus
  }
  subType: 'BEFORE_PURCHASE' | 'SERVICE'
  statusName: string
  hasPayment: boolean
  name: string
  type: TAppealType
  typeName: string
  createdDate: Date
  updatedDate: Date
  approvedOffer?: TServiceOffer | null
}

export type TAppealsList = TAppealInList[]

export type TEditAppealRequest = {
  uid: number
  orderId: string
  products?: TEditAppealProduct[] | []
  location?: TSuggest
  timeSlots?: TEditAppealTimeSlot[] | []
}

export type TEditAppealTimeSlot = {
  id?: number
  date?: string | null
  key?: string
  isValidDate?: boolean
}

export type TEditAppealProduct = {
  id: number
  parameters: TEditAppealProductParameter[] | []
}

export type TEditAppealProductParameter = {
  id: number
  parameterValue: {
    value: string | number | boolean | null
  }
}

export type TEditAppealResponse = {
  message: string
  code?: number
  errors?: [string]
  trace?: string
}

export type TGetAppealRequestBody = {
  uid: number | undefined
  orderId: string | null
}

export type TAppealFormImages = {
  id: string
  productId?: number
  paramId: number
  name: string
  serverUid: null | string
  previewUrl: string | null
  size: string
}

export type TAppealStepByClass = {
  [K in AppealStatus]: number
}

export type TAppealManagerRequest = {
  uid: number
  orderId: string
  comment: string
  datetime?: string
  url: 'make-complaint-dealer' | 'change-measurement-datetime' | 'cancel'
}

export type TAppealManagerResponse = {
  message?: string
  code?: number
  errors?: [string]
  trace?: string
  success?: boolean
  context?: {
    location?: string
    status?: string
  }
}

export enum AppealLoadingList {
  appealInformation = 'appealInformation',
  appealManager = 'appealManager',
  appealOffers = 'appealOffers',
  appealRecalcBlock = 'appealRecalcBlock',
}

export type TAppoveOfferRequest = {
  uid: number
  orderId: string
  offerId: number
}

export type TPaymentConfirmation = {
  uid: number
  orderId?: string
  total: number
}

export type TPaymentConfirmationResponse =
  | {
      success: boolean
    }
  | Error

export type TGetConsultationResponse = {
  canGrade: boolean
  comment: string
  date: string
  files: TProductFile[]
  grade: {
    value: number
    comment: string
    link: string
    files?: TProductFile[]
  }
  products: {
    id: number
    name: string
  }[]
  id: number
  status: {
    status: AppealStatus
    name: string
  }
}

export type TRecalculationRequestBody = {
  offerId: number
  uid: number
  orderId: string
}

export type TRecalculationResponse = {
  data: {
    success: boolean
  }
}
